// meeting-guides.ts — Dérivations pures des guides d'une réunion.
//
// Groupement par phase, taux de complétion, cycle d'états. Extrait du rendu
// pour être testable sans DOM : AUCUN accès à window/document, et aucun appel
// à `t()` — le module retourne des données, le module de page les met en mots.

export type GuidePhase = 'prepare' | 'animate' | 'conclude';
export type GuideStatus = 'pending' | 'done' | 'skipped';

export interface GuideItem {
  id: number;
  code: string | null;
  name: string;
  description: string;
  phase: GuidePhase;
  position: number;
  status: GuideStatus;
  checked_by: number | null;
  checked_by_name: string | null;
  checked_at: string | null;
  /** État posé par Ornexia (durée reçue dans l'invitation), pas par un humain. */
  prepositioned: boolean;
  /** Une action standard a déjà été créée depuis ce guide sur cette
   *  réunion — par n'importe quel animateur. Le CTA ne se repropose plus. */
  action_created: boolean;
  /** Modèle d'action, lu sur la définition vivante. Vide = pas de CTA. */
  action_label: string;
  action_for_animators: boolean;
  action_for_participants: boolean;
  /** Écart signé, en jours, par rapport à la date de réunion. */
  action_delay_days: number;
  /** Préfixer le libellé de l'action par « [réunion - date] ». */
  action_quote_meeting: boolean;
}

/** Ordre d'affichage des phases — le déroulé d'une réunion. */
export const PHASE_ORDER: readonly GuidePhase[] = ['prepare', 'animate', 'conclude'] as const;

export interface PhaseGroup {
  phase: GuidePhase;
  items: GuideItem[];
}

/**
 * Regroupe les guides par phase, dans l'ordre du déroulé. Les phases sans
 * guide sont omises (un tenant peut n'en sélectionner aucune en « Animer »).
 * À l'intérieur d'une phase, l'ordre propre à la réunion est respecté.
 */
export function groupByPhase(items: readonly GuideItem[]): PhaseGroup[] {
  const groups: PhaseGroup[] = [];
  for (const phase of PHASE_ORDER) {
    const inPhase = items
      .filter((g) => g.phase === phase)
      .sort((a, b) => a.position - b.position || a.id - b.id);
    if (inPhase.length) groups.push({ phase, items: inPhase });
  }
  return groups;
}

export interface GuideSummary {
  total: number;
  done: number;
  skipped: number;
  pending: number;
  /** Traités = exécutés + ignorés : dans les deux cas l'animateur a statué. */
  treated: number;
  /** Part des guides traités, en pourcentage entier. 0 si aucun guide. */
  pct: number;
}

export function summarize(items: readonly GuideItem[]): GuideSummary {
  const total = items.length;
  const done = items.filter((g) => g.status === 'done').length;
  const skipped = items.filter((g) => g.status === 'skipped').length;
  const treated = done + skipped;
  return {
    total,
    done,
    skipped,
    pending: total - treated,
    treated,
    pct: total ? Math.round((treated / total) * 100) : 0,
  };
}

/**
 * État suivant dans le cycle de clic : non traité → exécuté → ignoré → non
 * traité. Réversible dans les deux sens par construction, sans état d'échec —
 * la feature relève du conseil, pas de la sanction.
 */
export function nextStatus(current: GuideStatus): GuideStatus {
  if (current === 'pending') return 'done';
  if (current === 'done') return 'skipped';
  return 'pending';
}

/**
 * Échéance propre d'un guide : la date de réunion décalée de son délai.
 *
 * `action_delay_days` est un écart SIGNÉ par rapport à la réunion — négatif en
 * préparation (l'ordre du jour se rédige 7 jours avant), positif après (le CR
 * se valide le lendemain). L'échéance du guide est donc la même que celle
 * de l'action qu'il propose de créer : une seule date à retenir.
 */
export function guideDeadline(meetingDate: string, delayDays: number): string {
  // Arithmétique en UTC de bout en bout : minuit LOCAL relu en UTC recule d'un
  // jour dès qu'on est à l'est de Greenwich (Paris en été) — l'échéance
  // sautait alors une journée.
  const d = new Date(meetingDate + 'T00:00:00Z');
  d.setUTCDate(d.getUTCDate() + (delayDays || 0));
  return d.toISOString().slice(0, 10);
}

/**
 * Vrai si le guide aurait déjà dû être traité : son échéance est passée
 * alors qu'il est toujours « à traiter ».
 *
 * Un guide exécuté ou ignoré n'est jamais en retard — dans les deux cas
 * l'animateur a statué, et la feature relève du conseil, pas de la sanction.
 * `today` est injecté (format YYYY-MM-DD) : la fonction reste pure et testable
 * sans horloge.
 */
export function isGuideOverdue(
  guide: Pick<GuideItem, 'status' | 'action_delay_days'>,
  meetingDate: string | null,
  today: string,
): boolean {
  if (guide.status !== 'pending' || !meetingDate) return false;
  return today > guideDeadline(meetingDate, guide.action_delay_days);
}
